import { useMemo, useState } from 'react';
import type { DashboardData } from '../api/types';

/**
 * Stacked monthly bars, one segment per lead status.
 *
 * Hand-rolled like the other charts here rather than a library — see the
 * note in charts.tsx. The legend doubles as the filter: clicking a status
 * isolates it (everyone asks "show me just the DNPs" sooner or later), and
 * clicking it again goes back to the full stack.
 */

type MonthlyEntry = DashboardData['monthly'][number];

interface Segment {
  monthIndex: number;
  key: string;
  name: string;
  color: string;
  count: number;
}

export function MonthlyStatusChart({
  data,
  height = 280,
}: {
  data: MonthlyEntry[];
  height?: number;
}) {
  const [isolated, setIsolated] = useState<string | null>(null);
  const [hover, setHover] = useState<Segment | null>(null);
  const [pinned, setPinned] = useState<Segment | null>(null);

  const statuses = useMemo(
    () => (data[0]?.by_status ?? []).map((s) => ({ key: s.key, name: s.name, color: s.color })),
    [data],
  );
  const shownStatuses = isolated ? statuses.filter((s) => s.key === isolated) : statuses;

  const width = 720;
  const pad = { top: 14, right: 14, bottom: 30, left: 40 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const { barTotals, max, ticks } = useMemo(() => {
    const totals = data.map((m) =>
      m.by_status
        .filter((s) => !isolated || s.key === isolated)
        .reduce((sum, s) => sum + s.count, 0),
    );
    const rawMax = Math.max(1, ...totals);
    const step = Math.pow(10, Math.floor(Math.log10(rawMax)));
    const ceiling = Math.max(step, Math.ceil(rawMax / step) * step);
    return { barTotals: totals, max: ceiling, ticks: [0, ceiling / 2, ceiling] };
  }, [data, isolated]);

  const n = Math.max(1, data.length);
  const groupW = plotW / n;
  const barW = Math.min(52, groupW * 0.58);

  const active = pinned ?? hover;

  function isDimmed(key: string): boolean {
    return active !== null && active.key !== key;
  }

  return (
    <div>
      <figure className="chart" style={{ margin: 0 }} onMouseLeave={() => setHover(null)}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label={`Leads received by month, ${data.length} months, broken down by status`}
        >
          {ticks.map((t) => {
            const y = pad.top + plotH - (t / max) * plotH;
            return (
              <g key={t}>
                <line x1={pad.left} y1={y} x2={width - pad.right} y2={y} stroke="var(--rule-soft)" strokeWidth="1" />
                <text x={pad.left - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="var(--muted)">
                  {Math.round(t)}
                </text>
              </g>
            );
          })}

          {data.map((month, i) => {
            const x = pad.left + groupW * i + (groupW - barW) / 2;
            let cumulative = 0;
            const total = barTotals[i] ?? 0;

            return (
              <g key={month.month}>
                {shownStatuses.map((s) => {
                  const stat = month.by_status.find((b) => b.key === s.key);
                  const count = stat?.count ?? 0;
                  const segH = max > 0 ? (count / max) * plotH : 0;
                  const y = pad.top + plotH - cumulative - segH;
                  cumulative += segH;
                  const dimmed = isDimmed(s.key);

                  return (
                    <rect
                      key={s.key}
                      x={x}
                      y={y}
                      width={barW}
                      height={Math.max(segH, count > 0 ? 1.5 : 0)}
                      fill={s.color}
                      opacity={dimmed ? 0.35 : 1}
                      stroke="var(--surface)"
                      strokeWidth={shownStatuses.length > 1 ? 1.5 : 0}
                      style={{ cursor: 'pointer', transition: 'opacity .12s ease' }}
                      onMouseEnter={() => setHover({ monthIndex: i, key: s.key, name: s.name, color: s.color, count })}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPinned((p) =>
                          p && p.monthIndex === i && p.key === s.key
                            ? null
                            : { monthIndex: i, key: s.key, name: s.name, color: s.color, count },
                        );
                      }}
                    >
                      <title>{`${month.label} · ${s.name}: ${count.toLocaleString()}`}</title>
                    </rect>
                  );
                })}

                <text
                  x={x + barW / 2}
                  y={height - 8}
                  textAnchor="middle"
                  fontSize="11"
                  fill="var(--muted)"
                  fontWeight={total > 0 ? 650 : 400}
                >
                  {month.label}
                </text>
              </g>
            );
          })}
        </svg>

        {active && (
          <div
            className="chart-tooltip"
            style={{
              left: `${((pad.left + groupW * active.monthIndex + groupW / 2) / width) * 100}%`,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: active.color, flex: 'none' }} />
            <span>
              <strong>{active.count.toLocaleString()}</strong> {active.name.toLowerCase()}
              <span>{data[active.monthIndex]?.label}{pinned ? ' · click to unpin' : ''}</span>
            </span>
          </div>
        )}
      </figure>

      <div className="status-legend">
        {statuses.map((s) => {
          const on = isolated === s.key;
          return (
            <button
              key={s.key}
              type="button"
              className={`legend-chip${on ? ' isolated' : ''}${isolated && !on ? ' dimmed' : ''}`}
              style={on ? { background: s.color, borderColor: s.color } : undefined}
              onClick={() => setIsolated((cur) => (cur === s.key ? null : s.key))}
              title={on ? `Showing only ${s.name} — click to show all statuses` : `Show only ${s.name}`}
            >
              <span className="dot" style={{ background: on ? '#fff' : s.color }} />
              {s.name}
            </button>
          );
        })}
        {isolated && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setIsolated(null)}>
            Show all statuses
          </button>
        )}
      </div>
    </div>
  );
}
